<template>
  <div class="income-log-page" @click="closeOverlays">
    <div class="page-title-bar">
      <h1 class="page-title">Income Log</h1>
    </div>

    <div class="page-body">
      <div class="filter-section">
        <div class="filter-row filter-row-primary">
          <div class="filter-item field-search-item" :class="{ 'is-open': openDd === 'field' }">
            <span
              role="button"
              tabindex="0"
              class="field-type-btn"
              @click.stop="toggleDd('field')"
              @keydown.enter.prevent="toggleDd('field')"
              @keydown.space.prevent="toggleDd('field')"
            >
              <span class="label">{{ currentSearchFieldLabel }}</span>
              <img :src="imgDown" class="icon downarrow-inline" alt="" />
            </span>
            <span class="field-split" aria-hidden="true" />
            <input
              v-model="form.searchQuery"
              type="text"
              placeholder="Search.."
              @keydown.enter.prevent="runSearch"
              @focus="openDd = null"
            />
            <ul v-if="openDd === 'field'" class="dropdown-panel" @click.stop>
              <li
                v-for="opt in searchFieldOptions"
                :key="opt.value"
                :class="{ active: form.searchField === opt.value }"
                @click="selectSearchField(opt.value)"
              >
                {{ opt.label }}
              </li>
            </ul>
          </div>

          <div
            class="filter-item select-item"
            :class="{ 'is-open': openDd === 'category' }"
            @click.stop="toggleDd('category')"
          >
            <span class="label">Service Category:</span>
            <input
              type="text"
              readonly
              :value="serviceCategoryLabel"
              :class="{ 'is-placeholder': form.serviceCategory === 'All' }"
            />
            <img :src="imgDown" class="icon downarrow" alt="" />
            <ul v-if="openDd === 'category'" class="dropdown-panel" @click.stop>
              <li
                v-for="opt in serviceCategoryOptions"
                :key="opt.value"
                :class="{ active: form.serviceCategory === opt.value }"
                @click="selectOption('serviceCategory', opt.value)"
              >
                {{ opt.label }}
              </li>
            </ul>
          </div>

          <div
            class="filter-item select-item"
            :class="{ 'is-open': openDd === 'type' }"
            @click.stop="toggleDd('type')"
          >
            <span class="label">Transaction Type:</span>
            <input
              type="text"
              readonly
              :value="transactionTypeLabel"
              :class="{ 'is-placeholder': form.transactionType === 'All' }"
            />
            <img :src="imgDown" class="icon downarrow" alt="" />
            <ul v-if="openDd === 'type'" class="dropdown-panel" @click.stop>
              <li
                v-for="opt in transactionTypeOptions"
                :key="opt.value"
                :class="{ active: form.transactionType === opt.value }"
                @click="selectOption('transactionType', opt.value)"
              >
                {{ opt.label }}
              </li>
            </ul>
          </div>

          <div
            class="filter-item select-item"
            :class="{ 'is-open': openDd === 'payment' }"
            @click.stop="toggleDd('payment')"
          >
            <span class="label">Payment Method:</span>
            <input
              type="text"
              readonly
              :value="paymentMethodLabel"
              :class="{ 'is-placeholder': form.paymentMethod === 'All' }"
            />
            <img :src="imgDown" class="icon downarrow" alt="" />
            <ul v-if="openDd === 'payment'" class="dropdown-panel" @click.stop>
              <li
                v-for="opt in paymentMethodOptions"
                :key="opt.value"
                :class="{ active: form.paymentMethod === opt.value }"
                @click="selectOption('paymentMethod', opt.value)"
              >
                {{ opt.label }}
              </li>
            </ul>
          </div>
        </div>

        <div class="filter-row filter-row-bottom">
          <div class="filter-item date-wrapper" :class="{ 'is-open': openCal }">
            <div class="date-calendar-anchor">
              <div class="date-item-box" @click.stop="toggleCal">
                <input
                  type="text"
                  readonly
                  :value="dateDisplay"
                  placeholder="Select a date"
                  :class="{ 'is-placeholder': !form.date }"
                />
                <span class="date-cal-split" aria-hidden="true" />
                <img :src="imgCal" class="calendar-icon" alt="" />
              </div>
              <div v-if="openCal" class="calendar-pop" @click.stop>
                <CalendarMini
                  v-model:month="calMonth"
                  v-model:year="calYear"
                  :selected-ymd="form.date"
                  @pick="onPickDate"
                />
              </div>
            </div>
          </div>
          <div class="btn-group">
            <button type="button" class="search-btn" @click="runSearch">Search</button>
            <button type="button" class="clear-btn" @click="clearFilters">Clear</button>
          </div>
        </div>
      </div>

      <LoadingIndicator v-if="loading" />
      <div v-else-if="tableRows.length === 0" class="orders-empty">
        <img :src="imgListBlank" class="orders-empty-img" alt="" />
        <p class="orders-empty-msg">No transactions found</p>
      </div>
      <div v-else class="table-section">
        <div
          ref="tableScrollRef"
          class="table-scroll"
          :class="{ 'is-dragging': isDragging }"
          @mousedown="onTableDragStart"
        >
          <table class="income-table">
            <colgroup>
              <col class="col-id" />
              <col class="col-category" />
              <col class="col-type" />
              <col class="col-subtotal" />
              <col class="col-discount" />
              <col class="col-coupon" />
              <col class="col-tax" />
              <col class="col-total" />
              <col class="col-pay" />
              <col class="col-time" />
            </colgroup>
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Service Category</th>
                <th>Transaction Type</th>
                <th>Subtotal</th>
                <th>Discount</th>
                <th>Coupon</th>
                <th>Tax</th>
                <th>Total</th>
                <th>Payment Method</th>
                <th class="col-sticky-time">Transaction Time</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableRows" :key="row.id">
                <td>
                  <a
                    href="#"
                    class="cell-link"
                    @click.prevent="handleTransactionClick(row)"
                  >{{ row.transactionId }}</a>
                </td>
                <td>{{ row.serviceCategory }}</td>
                <td>{{ row.transactionType }}</td>
                <td>{{ row.subtotal }}</td>
                <td>{{ row.discount }}</td>
                <td>
                  <template v-if="row.coupon === 'N/A'">N/A</template>
                  <a
                    v-else-if="row.couponId"
                    href="#"
                    class="cell-link"
                    @click.prevent="handleCouponClick(row)"
                  >{{ row.coupon }}</a>
                  <span v-else class="cell-link cell-link--static">{{ row.coupon }}</span>
                </td>
                <td>{{ row.tax }}</td>
                <td>{{ row.total }}</td>
                <td>{{ row.paymentMethod }}</td>
                <td class="col-sticky-time">{{ row.transactionTime }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pagination-wrapper">
        <PageComponent
          v-if="!loading && totalPagesFromApi > 0"
          :current-page="currentPage"
          :total-pages="totalPagesFromApi"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import PageComponent from '@/components/PageComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import CalendarMini from './components/CalendarMini.vue'
import {
  buildIncomePaymentMethods,
  buildIncomeSearchTypes,
  buildIncomeServiceCategories,
  buildIncomeTransactionTypes,
  DEFAULT_INCOME_SEARCH_TYPES,
  DEFAULT_PAYMENT_METHODS,
  DEFAULT_SERVICE_CATEGORIES,
  DEFAULT_TRANSACTION_TYPES,
  postIncomeLogDetail,
  postIncomeLogList,
  postIncomeLogMeta,
  resolveIncomeFilterLabel,
  type IncomeLogListItem,
  type IncomeLogListParams,
  type IncomeLogMetaOption,
} from '@/api/incomeLog'
import { careOrdersDetailRoute } from '@/utils/careOrdersRoutes'
import { couponDetailRoute } from '@/utils/libraryRoutes'
import { writeHmCareOrdersListPrefill } from '@/api/CareOrderDetailFocus'
import { writeCouponCenterListPrefill } from '@/api/CouponCenter'
import imgDown from '@/assets/downarrow@2x.png'
import imgCal from '@/assets/编组 2@2x.png'
import imgListBlank from '@/assets/list_blank@2x.png'

type OpenDd = 'field' | 'category' | 'type' | 'payment' | null

interface IncomeRow {
  id: string
  transactionId: string
  orderId: string
  couponId: string
  serviceCategory: string
  transactionType: string
  subtotal: string
  discount: string
  coupon: string
  tax: string
  total: string
  paymentMethod: string
  transactionTime: string
}

const router = useRouter()
const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => {},
)

const PAGE_SIZE = 10
const LOADING_FLASH_MS = 280

const now = new Date()
const calMonth = ref(now.getMonth())
const calYear = ref(now.getFullYear())
const openDd = ref<OpenDd>(null)
const openCal = ref(false)
const currentPage = ref(1)
const totalPagesFromApi = ref(1)
const loading = ref(true)
const tableRows = ref<IncomeRow[]>([])
let fetchGeneration = 0

const searchFieldOptions = ref<IncomeLogMetaOption[]>([...DEFAULT_INCOME_SEARCH_TYPES])
const serviceCategoryOptions = ref<IncomeLogMetaOption[]>([...DEFAULT_SERVICE_CATEGORIES])
const transactionTypeOptions = ref<IncomeLogMetaOption[]>([...DEFAULT_TRANSACTION_TYPES])
const paymentMethodOptions = ref<IncomeLogMetaOption[]>([...DEFAULT_PAYMENT_METHODS])

const form = reactive({
  searchField: DEFAULT_INCOME_SEARCH_TYPES[0]?.value ?? 'transaction_id',
  searchQuery: '',
  serviceCategory: 'All',
  transactionType: 'All',
  paymentMethod: 'All',
  date: '',
})

const currentSearchFieldLabel = computed(() => {
  const hit = searchFieldOptions.value.find((o) => o.value === form.searchField)
  return hit?.label ?? 'Transaction ID'
})

const serviceCategoryLabel = computed(() =>
  resolveIncomeFilterLabel(serviceCategoryOptions.value, form.serviceCategory),
)
const transactionTypeLabel = computed(() =>
  resolveIncomeFilterLabel(transactionTypeOptions.value, form.transactionType),
)
const paymentMethodLabel = computed(() =>
  resolveIncomeFilterLabel(paymentMethodOptions.value, form.paymentMethod),
)

const dateDisplay = computed(() => {
  if (!form.date) return ''
  const [y, m, d] = form.date.split('-').map(Number)
  if (!y || !m || !d) return form.date
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
})

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function formatMoney(value: string | number | null | undefined): string {
  if (value == null || value === '') return 'N/A'
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || 'N/A'
  }
  if (Number.isNaN(value)) return 'N/A'
  const abs = Math.abs(value).toFixed(2)
  return value < 0 ? `-$${abs}` : `$${abs}`
}

function resolveIncomeText(
  text: string | undefined,
  amount: string | number | null | undefined,
): string {
  const fromText = text?.trim()
  if (fromText) return fromText
  return formatMoney(amount)
}

function mapListItemToRow(item: IncomeLogListItem): IncomeRow {
  return {
    id: String(item.id),
    transactionId: String(item.transaction_id ?? item.id ?? ''),
    orderId: String(item.order_id ?? '').trim(),
    couponId: String(item.coupon_id ?? '').trim(),
    serviceCategory: item.service_category_text || item.service_category || '',
    transactionType: item.transaction_type_text || item.transaction_type || '',
    subtotal: resolveIncomeText(item.subtotal_text, item.subtotal_amount ?? item.subtotal),
    discount: resolveIncomeText(item.discount_text, item.discount_amount ?? item.discount),
    coupon: resolveIncomeText(item.coupon_text, item.coupon_amount ?? item.coupon),
    tax: resolveIncomeText(item.tax_text, item.tax_amount ?? item.tax),
    total: resolveIncomeText(item.total_text, item.total_amount ?? item.total),
    paymentMethod: item.payment_method_text || item.payment_method || '',
    transactionTime: item.transaction_time || '',
  }
}

function ensureSearchFieldValid(): void {
  if (!searchFieldOptions.value.some((o) => o.value === form.searchField)) {
    form.searchField = searchFieldOptions.value[0]?.value ?? 'transaction_id'
  }
}

function buildListParams(): IncomeLogListParams {
  const params: IncomeLogListParams = {
    page: currentPage.value,
    page_size: PAGE_SIZE,
  }
  const keyword = form.searchQuery.trim()
  if (keyword) {
    params.search_type = form.searchField
    params.keyword = keyword
  }
  if (form.serviceCategory && form.serviceCategory !== 'All') {
    params.service_category = form.serviceCategory
  }
  if (form.transactionType && form.transactionType !== 'All') {
    params.transaction_type = form.transactionType
  }
  if (form.paymentMethod && form.paymentMethod !== 'All') {
    params.payment_method = form.paymentMethod
  }
  if (form.date) {
    params.date = form.date
  }
  return params
}

async function loadMeta(): Promise<void> {
  try {
    const response = await postIncomeLogMeta({ page: 1, page_size: PAGE_SIZE })
    if (response.code === 200 && response.data) {
      searchFieldOptions.value = buildIncomeSearchTypes(response.data)
      serviceCategoryOptions.value = buildIncomeServiceCategories(response.data)
      transactionTypeOptions.value = buildIncomeTransactionTypes(response.data)
      paymentMethodOptions.value = buildIncomePaymentMethods(response.data)
      ensureSearchFieldValid()
      return
    }
  } catch (err) {
    console.error('Failed to load income-log meta:', err)
  }
  searchFieldOptions.value = [...DEFAULT_INCOME_SEARCH_TYPES]
  serviceCategoryOptions.value = [...DEFAULT_SERVICE_CATEGORIES]
  transactionTypeOptions.value = [...DEFAULT_TRANSACTION_TYPES]
  paymentMethodOptions.value = [...DEFAULT_PAYMENT_METHODS]
  ensureSearchFieldValid()
}

async function fetchList(): Promise<void> {
  const generation = ++fetchGeneration
  loading.value = true
  try {
    await wait(LOADING_FLASH_MS)
    const response = await postIncomeLogList(buildListParams())
    if (generation !== fetchGeneration) return

    if (response.code === 200) {
      tableRows.value = (response.data?.list || []).map(mapListItemToRow)
      totalPagesFromApi.value = Math.max(1, response.data?.pagination?.total_pages ?? 1)
    } else {
      tableRows.value = []
      totalPagesFromApi.value = 1
      showToast('error', response.msg || 'Failed to fetch')
    }
  } catch (err) {
    if (generation !== fetchGeneration) return
    console.error('Income log list error:', err)
    tableRows.value = []
    totalPagesFromApi.value = 1
    showToast('error', 'Network request failed')
  } finally {
    if (generation === fetchGeneration) {
      loading.value = false
    }
  }
}

async function beginDataLoad(): Promise<void> {
  await loadMeta()
  await fetchList()
}

function toggleDd(key: Exclude<OpenDd, null>): void {
  openCal.value = false
  openDd.value = openDd.value === key ? null : key
}

function selectSearchField(value: string): void {
  form.searchField = value
  openDd.value = null
}

function selectOption(
  key: 'serviceCategory' | 'transactionType' | 'paymentMethod',
  value: string,
): void {
  form[key] = value
  openDd.value = null
}

function toggleCal(): void {
  openDd.value = null
  openCal.value = !openCal.value
  if (openCal.value && form.date) {
    const [y, m] = form.date.split('-').map(Number)
    if (y && m) {
      calYear.value = y
      calMonth.value = m - 1
    }
  }
}

function onPickDate(ymd: string): void {
  form.date = ymd
  openCal.value = false
}

function closeOverlays(): void {
  openDd.value = null
  openCal.value = false
}

function runSearch(): void {
  currentPage.value = 1
  closeOverlays()
  void fetchList()
}

function clearFilters(): void {
  form.searchField = searchFieldOptions.value[0]?.value ?? 'transaction_id'
  form.searchQuery = ''
  form.serviceCategory = 'All'
  form.transactionType = 'All'
  form.paymentMethod = 'All'
  form.date = ''
  currentPage.value = 1
  closeOverlays()
  void fetchList()
}

function onPageChange(page: number): void {
  currentPage.value = page
  void fetchList()
}

async function resolveOrderId(row: IncomeRow): Promise<string> {
  if (row.orderId) return row.orderId

  try {
    const response = await postIncomeLogDetail(row.id)
    if (response.code === 200 && response.data) {
      const orderId = String(response.data.order_id ?? '').trim()
      if (orderId) return orderId
      const txn = String(response.data.transaction_id ?? '').trim()
      if (txn) return txn
    }
  } catch (err) {
    console.error('Failed to load income-log detail:', err)
  }
  return ''
}

async function handleTransactionClick(row: IncomeRow): Promise<void> {
  const orderId = await resolveOrderId(row)
  if (!orderId) {
    showToast('error', 'Order not found for this transaction')
    return
  }
  writeHmCareOrdersListPrefill({
    searchField: 'order_id',
    searchQuery: orderId,
    careSubKey: 'hm_orders',
  })
  void router.push(careOrdersDetailRoute(orderId))
}

function handleCouponClick(row: IncomeRow): void {
  const couponId = row.couponId.trim()
  if (!couponId) return
  writeCouponCenterListPrefill({
    searchField: 'coupon_code',
    searchQuery: couponId,
  })
  void router.push(couponDetailRoute(couponId))
}

const tableScrollRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)
let dragStartX = 0
let dragScrollLeft = 0

function onTableDragStart(e: MouseEvent): void {
  if (e.button !== 0) return
  const target = e.target as HTMLElement
  if (target.closest('a, button, input')) return
  const el = tableScrollRef.value
  if (!el) return
  isDragging.value = true
  dragStartX = e.clientX
  dragScrollLeft = el.scrollLeft
  window.addEventListener('mousemove', onTableDragMove)
  window.addEventListener('mouseup', onTableDragEnd)
}

function onTableDragMove(e: MouseEvent): void {
  if (!isDragging.value || !tableScrollRef.value) return
  const dx = e.clientX - dragStartX
  tableScrollRef.value.scrollLeft = dragScrollLeft - dx
}

function onTableDragEnd(): void {
  isDragging.value = false
  window.removeEventListener('mousemove', onTableDragMove)
  window.removeEventListener('mouseup', onTableDragEnd)
}

function onDocClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (
    target.closest('.filter-item.select-item') ||
    target.closest('.field-type-btn') ||
    target.closest('.dropdown-panel') ||
    target.closest('.date-item-box') ||
    target.closest('.calendar-pop')
  ) {
    return
  }
  closeOverlays()
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  void beginDataLoad()
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  window.removeEventListener('mousemove', onTableDragMove)
  window.removeEventListener('mouseup', onTableDragEnd)
})
</script>

<style scoped>
.income-log-page {
  width: 100%;
  box-sizing: border-box;
  background: #f5f6fb;
}

.page-title-bar {
  box-sizing: border-box;
  padding: 0 0 16px;
}

.page-title {
  margin: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #000;
}

.page-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-section {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 16px;
  box-sizing: border-box;
  overflow: visible;
}

.filter-row {
  display: grid;
  gap: 16px;
  align-items: center;
}

.filter-row-primary {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.filter-row-bottom {
  margin-top: 16px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.filter-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  min-width: 0;
  height: 32px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 0 16px;
  transition: border-color 0.15s ease;
}

.filter-item:focus-within,
.filter-item.is-open {
  border-color: #666;
}

.field-search-item {
  gap: 0;
  padding: 0 16px;
}

.field-type-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  cursor: pointer;
  outline: none;
}

.field-type-btn .label,
.select-item .label {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #000;
  white-space: nowrap;
}

.field-split,
.date-cal-split {
  flex-shrink: 0;
  width: 1px;
  height: 16px;
  margin: 0 8px;
  background: #e0e0e0;
}

.field-search-item input,
.select-item input,
.date-item-box input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #000;
  padding: 0;
}

.field-search-item input::placeholder,
.date-item-box input::placeholder {
  color: #ccc;
  font-weight: 400;
}

.select-item input {
  cursor: pointer;
  padding-right: 20px;
}

.select-item input.is-placeholder,
.date-item-box input.is-placeholder {
  color: #ccc;
  font-weight: 400;
  -webkit-text-fill-color: #ccc;
}

.icon {
  width: 12px;
  height: 12px;
  object-fit: contain;
}

.downarrow-inline {
  flex-shrink: 0;
}

.downarrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 30;
  box-sizing: border-box;
  scrollbar-width: none;
}

.dropdown-panel::-webkit-scrollbar {
  display: none;
}

.dropdown-panel li {
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 400;
  color: #000;
  cursor: pointer;
}

.dropdown-panel li:hover {
  background: #f5f5f5;
}

.dropdown-panel li.active {
  font-weight: 600;
  color: #000;
}

.date-wrapper {
  padding: 0;
  border: none;
  background: transparent;
  height: auto;
}

.date-wrapper:focus-within,
.date-wrapper.is-open {
  border-color: transparent;
}

.date-calendar-anchor {
  position: relative;
  width: 100%;
}

.date-item-box {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  height: 32px;
  padding: 0 16px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  transition: border-color 0.15s ease;
}

.date-item-box:focus-within,
.date-wrapper.is-open .date-item-box {
  border-color: #666;
}

.date-item-box input {
  cursor: pointer;
}

.calendar-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
  cursor: pointer;
}

.calendar-pop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 35;
}

.btn-group {
  display: flex;
  align-items: center;
  gap: 16px;
  grid-column: 4;
  justify-self: end;
}

.search-btn,
.clear-btn {
  box-sizing: border-box;
  width: 80px;
  height: 32px;
  border-radius: 16px;
  font-family: SFPro, -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-weight: bold;
  line-height: 24px;
  cursor: pointer;
}

.search-btn {
  background: #000;
  color: #fff;
  border: none;
}

.search-btn:hover {
  background: #484848;
}

.clear-btn {
  background: #fff;
  color: #666;
  border: 1px solid #e0e0e0;
}

.clear-btn:hover {
  background: #f5f5f5;
}

.table-section {
  background: #fff;
  border-radius: 16px;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
  cursor: grab;
  scrollbar-width: none;
  -ms-overflow-style: none;
  user-select: none;
}

.table-scroll::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.table-scroll.is-dragging {
  cursor: grabbing;
}

.income-table {
  --col-160: 160px;
  --col-120: 120px;
  --time-w: 252px;
  --tax-w: var(--col-120);
  --total-w: var(--col-120);
  --pay-w: var(--col-160);
  /* 半个 Tax + Total + Payment Method：初始折在 Tax 一半处 */
  --fold-extra: calc(var(--tax-w) / 2 + var(--total-w) + var(--pay-w));
  width: calc(100% + var(--fold-extra));
  min-width: calc(100% + var(--fold-extra));
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.income-table .col-id,
.income-table .col-category,
.income-table .col-type,
.income-table .col-pay {
  width: var(--col-160);
}

.income-table .col-subtotal,
.income-table .col-discount,
.income-table .col-coupon,
.income-table .col-tax,
.income-table .col-total {
  width: var(--col-120);
}

.income-table .col-time {
  width: var(--time-w);
}

.income-table th,
.income-table td {
  box-sizing: border-box;
  height: 56px;
  padding: 0 12px;
  font-family: SFPro, SFPro, -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  font-style: normal;
  white-space: nowrap;
  border: none;
  vertical-align: middle;
  background: #ffffff;
  box-shadow: inset 0 -1px 0 0 #e0e0e0;
  overflow: hidden;
  text-overflow: clip;
}

.income-table th {
  font-weight: bold;
  text-align: center;
}

.income-table th:first-child {
  width: var(--col-160);
  border-radius: 16px 0 0 0;
}

.income-table td {
  font-weight: 400;
  text-align: center;
}

.income-table tbody tr:last-child td {
  box-shadow: none;
}

.income-table .col-sticky-time {
  position: sticky;
  right: 0;
  z-index: 2;
  width: var(--time-w);
  min-width: var(--time-w);
  max-width: var(--time-w);
  background: #ffffff;
  box-shadow:
    inset 0 -1px 0 0 #e0e0e0,
    -4px 0 10px rgba(0, 0, 0, 0.06);
}

.income-table thead .col-sticky-time {
  z-index: 3;
  text-align: center;
  border-radius: 0 16px 0 0;
}

.income-table td.col-sticky-time {
  text-align: center;
}

.income-table tbody tr:last-child td.col-sticky-time {
  border-radius: 0 0 16px 0;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.06);
}

.cell-link {
  font-family: SFPro, SFPro, -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  font-style: normal;
  text-align: center;
  text-decoration-line: underline;
  cursor: pointer;
  user-select: auto;
  background-color: transparent;
}

.cell-link--static {
  cursor: default;
}

.cell-link:hover,
.cell-link:focus {
  background-color: transparent;
}

.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

.orders-empty-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.orders-empty-msg {
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 500;
  color: #b0b0b0;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 1200px) {
  .filter-row-primary,
  .filter-row-bottom {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .btn-group {
    grid-column: 2;
  }
}

@media (max-width: 720px) {
  .filter-row-primary,
  .filter-row-bottom {
    grid-template-columns: 1fr;
  }

  .btn-group {
    grid-column: auto;
    justify-self: stretch;
    justify-content: flex-end;
  }
}
</style>
